/**
 * Mã phạm vi chi nhánh — dạng chuỗi dùng chung cho mọi màn cần "chọn chi nhánh
 * theo cây": phạm vi của nhân sự (Casdoor) và phạm vi chụp của concept.
 *
 * Ba dạng mã:
 *   "all"            — toàn bộ, gồm cả miền/cơ sở mở sau này
 *   "group-<code>"   — cả một nhóm (miền), suy thành viên từ cây
 *   "branch-<code>"  — đúng một cơ sở
 *
 * Nhóm được lưu là NHÓM, không bung thành danh sách cơ sở: bung ra thì thêm cơ
 * sở vào miền sẽ không tự có hiệu lực, phải vào sửa tay từng bản ghi.
 *
 * Toàn bộ file này là hàm thuần — không React, không hook — để test được mà
 * không cần dựng component hay mock API.
 */

export type ScopeUnit = {
  code: string;
  name: string;
  kind: "group" | "branch";
  parentCode?: string;
  isActive?: boolean;
  sortOrder?: number;
};

export type ScopeTreeRow = { unit: ScopeUnit; depth: number };

export type ScopeTreeIndex = {
  /** "group-x" → các "branch-y" nằm dưới nó (mọi độ sâu). */
  descendantBranchScopes: Map<string, string[]>;
  /** "branch-y" → các "group-x" là tổ tiên của nó. */
  ancestorGroupScopes: Map<string, string[]>;
};

export const ALL_SCOPE = "all";

/**
 * Mã cơ sở THẬT — không phải `all`, không phải mã vùng `<nhóm>_all`.
 *
 * `staff.branch` (và `primaryBranch` của phiên) giữ được cả mã phạm vi: giám
 * đốc, kế toán, trưởng phòng toàn hệ mang `all`, quản lý cụm mang `hn_all`.
 * Màn làm việc theo MỘT cơ sở mà lấy thẳng mã đó để hỏi máy chủ là hỏi bảng
 * của một nơi không tồn tại (rà soát két 10/10/2026; kiểm thử 18/10/2026 với tài
 * khoản mang mã phạm vi: Bảng suất, Điểm hành vi, Điều phối ảnh thẻ, Kiểm cơ sở).
 */
export function isRealBranch(code: string | null | undefined): code is string {
  const c = (code ?? "").trim();
  return c !== "" && c !== ALL_SCOPE && !c.endsWith("_all");
}

/**
 * Cơ sở mở sẵn của màn làm việc theo MỘT cơ sở.
 *
 * 1. Cơ sở nhà là cơ sở thật ⇒ cơ sở nhà.
 * 2. Không (mã phạm vi) ⇒ cơ sở ĐẦU DANH SÁCH CHỌN nằm trong phạm vi máy chủ
 *    đã tính cho phiên (`branchScope.branches`). Phải soát phạm vi: dưới nguồn
 *    `database` danh sách chọn KHÔNG bị thu hẹp (xem `useScopedUnits`), nên cơ
 *    sở đầu danh sách có thể ngoài miền của người xem — quản lý cụm Hà Nội mở
 *    ra Trường Chinh chưa gán, trưởng cửa hàng miền Nam mở ra một cơ sở Hà Nội
 *    và ăn "Không tìm thấy cơ sở".
 *    Không cơ sở nào trong danh sách chọn thuộc phạm vi (danh sách chưa về, cơ
 *    sở đã ngưng) ⇒ cơ sở đầu của phạm vi: máy chủ trả đúng bảng của người xem.
 * 3. Không biết phạm vi ⇒ cơ sở đầu danh sách (rỗng khi chưa có danh sách).
 */
export function pickHomeBranch(
  primaryBranch: string | null | undefined,
  options: readonly string[],
  scopeBranches: readonly string[] = [],
): string {
  if (isRealBranch(primaryBranch)) return primaryBranch;
  const inScope = scopeBranches.filter(isRealBranch);
  if (inScope.length > 0) {
    return options.find((code) => inScope.includes(code)) ?? inScope[0]!;
  }
  return options[0] ?? "";
}

/** Mã phạm vi của một nút trong cây. */
export function branchScopeCode(unit: ScopeUnit): string {
  return unit.kind === "group" ? `group-${unit.code}` : `branch-${unit.code}`;
}

/**
 * Danh sách phẳng kèm độ sâu, đúng thứ tự hiển thị.
 *
 * Dùng danh sách phẳng thay vì cây lồng nhau vì phần vẽ chỉ cần thụt lề theo
 * `depth`; đệ quy trong JSX không mua thêm được gì.
 */
export function scopeTreeRows(units: readonly ScopeUnit[]): ScopeTreeRow[] {
  const children = new Map<string, ScopeUnit[]>();
  for (const unit of units) {
    const key = unit.parentCode ?? "";
    const rows = children.get(key) ?? [];
    rows.push(unit);
    children.set(key, rows);
  }
  const rows: ScopeTreeRow[] = [];
  const visit = (parentCode: string, depth: number) => {
    for (const unit of children.get(parentCode) ?? []) {
      rows.push({ unit, depth });
      visit(unit.code, depth + 1);
    }
  };
  visit("", 0);
  return rows;
}

/** Tra cứu tổ tiên/hậu duệ, tính sẵn một lần cho mỗi cây. */
export function buildScopeTreeIndex(units: readonly ScopeUnit[]): ScopeTreeIndex {
  const byCode = new Map(units.map((unit) => [unit.code, unit]));
  const children = new Map<string, ScopeUnit[]>();
  for (const unit of units) {
    const key = unit.parentCode ?? "";
    const rows = children.get(key) ?? [];
    rows.push(unit);
    children.set(key, rows);
  }

  const descendantBranchScopes = new Map<string, string[]>();
  const collectBranches = (unit: ScopeUnit, seen: Set<string>): string[] => {
    // Cây hỏng (cha trỏ vòng) không được làm treo màn hình.
    if (seen.has(unit.code)) return [];
    seen.add(unit.code);
    if (unit.kind === "branch") return [`branch-${unit.code}`];
    return (children.get(unit.code) ?? []).flatMap((child) =>
      collectBranches(child, seen),
    );
  };
  for (const unit of units) {
    if (unit.kind === "group") {
      descendantBranchScopes.set(`group-${unit.code}`, collectBranches(unit, new Set()));
    }
  }

  const ancestorGroupScopes = new Map<string, string[]>();
  for (const unit of units) {
    if (unit.kind !== "branch") continue;
    const ancestors: string[] = [];
    const seen = new Set<string>([unit.code]);
    let parentCode = unit.parentCode ?? "";
    while (parentCode) {
      if (seen.has(parentCode)) break;
      seen.add(parentCode);
      const parent = byCode.get(parentCode);
      if (!parent) break;
      if (parent.kind === "group") ancestors.push(`group-${parent.code}`);
      parentCode = parent.parentCode ?? "";
    }
    ancestorGroupScopes.set(`branch-${unit.code}`, ancestors);
  }

  return { descendantBranchScopes, ancestorGroupScopes };
}

/**
 * Bỏ mã thừa: đã chọn cả nhóm thì không giữ thêm cơ sở con của nhóm đó, và
 * "all" nuốt mọi mã khác. Sắp xếp để so sánh/ghi ra ổn định.
 */
export function normaliseScopeCodes(
  codes: readonly string[],
  tree: ScopeTreeIndex,
): string[] {
  if (codes.includes(ALL_SCOPE)) return [ALL_SCOPE];
  const selected = new Set(codes);
  for (const code of codes) {
    for (const branchScope of tree.descendantBranchScopes.get(code) ?? []) {
      selected.delete(branchScope);
    }
  }
  return [...selected].sort();
}

/**
 * Tick/bỏ tick một mã, giữ các mã loại trừ lẫn nhau.
 *
 * Phần lõi thuần của `toggle` trong BranchScopeDialog: "all" loại mọi mã khác,
 * chọn nhóm thì bỏ cơ sở con, chọn cơ sở thì bỏ nhóm cha — nếu không sẽ lưu ra
 * phạm vi mâu thuẫn kiểu "cả miền HN, nhưng cũng riêng hn_01".
 */
export function toggleScopeCode(
  current: readonly string[],
  code: string,
  checked: boolean,
  tree: ScopeTreeIndex,
): string[] {
  if (!checked) return current.filter((value) => value !== code);
  if (code === ALL_SCOPE) return [ALL_SCOPE];
  const next = new Set(current.filter((value) => value !== ALL_SCOPE));
  if (code.startsWith("group-")) {
    for (const branchScope of tree.descendantBranchScopes.get(code) ?? []) {
      next.delete(branchScope);
    }
  }
  if (code.startsWith("branch-")) {
    for (const groupScope of tree.ancestorGroupScopes.get(code) ?? []) {
      next.delete(groupScope);
    }
  }
  next.add(code);
  return normaliseScopeCodes([...next], tree);
}

/**
 * Phạm vi này có phủ mã cơ sở đó không.
 *
 * Ba tầng, theo đúng thứ tự: mã cơ sở → mã nhóm (kể cả dạng `<group>_all` lưu
 * ở `staff.branch`) → đoán theo tiền tố cho giá trị cũ chưa từng có trong danh
 * mục. Mã KHÔNG tra được thì trả `true` (không lọc), không trả `false`: dữ
 * liệu lạ chỉ nên làm bộ lọc rộng ra, chứ để nó nuốt sạch lựa chọn thì người
 * dùng mất hết concept mà không hiểu vì sao.
 */
export function scopeCoversBranch(
  scopes: readonly string[],
  branchCode: string | null | undefined,
  units: readonly ScopeUnit[],
): boolean {
  if (scopes.includes(ALL_SCOPE)) return true;
  const branch = (branchCode ?? "").trim().toLowerCase();
  // Chưa chọn cơ sở ⇒ không lọc, khớp quy ước `region == ""` của backend.
  if (!branch) return true;
  if (scopes.length === 0) return false;
  if (scopes.includes(`branch-${branch}`)) return true;

  const groupCodes = scopes
    .filter((scope) => scope.startsWith("group-"))
    .map((scope) => scope.slice("group-".length));
  if (groupCodes.length === 0) return false;

  const byCode = new Map(units.map((unit) => [unit.code, unit]));

  // Dạng "<group>_all" là phạm vi cả miền, không phải một cơ sở.
  const allSuffix = branch.endsWith("_all") ? branch.slice(0, -"_all".length) : null;
  if (allSuffix) {
    if (groupCodes.includes(allSuffix)) return true;
    return groupCodes.some((group) => isDescendantOf(allSuffix, group, byCode));
  }

  if (byCode.has(branch)) {
    return groupCodes.some((group) => isDescendantOf(branch, group, byCode));
  }

  // Giá trị tự do chưa từng vào danh mục: đoán theo tiền tố như lib/branches/tree.
  const guessed = branch.startsWith("hn")
    ? "hn"
    : branch.startsWith("sg") || branch.startsWith("hcm")
      ? "sg"
      : "";
  if (!guessed) return true; // không suy được ⇒ không lọc
  return groupCodes.includes(guessed);
}

/** `code` có nằm dưới `ancestor` không (mọi độ sâu). An toàn với cây trỏ vòng. */
function isDescendantOf(
  code: string,
  ancestor: string,
  byCode: ReadonlyMap<string, ScopeUnit>,
): boolean {
  const seen = new Set<string>();
  let current = byCode.get(code);
  while (current) {
    if (current.code === ancestor) return true;
    if (seen.has(current.code)) return false;
    seen.add(current.code);
    const parentCode = current.parentCode ?? "";
    if (!parentCode) return false;
    current = byCode.get(parentCode);
  }
  return false;
}

/**
 * Phạm vi có giao với một nhóm không — dùng cho tab lọc theo miền.
 *
 * Khác `scopeCoversBranch`: ở đây hỏi "có dính tới miền này không", nên một
 * phạm vi cấp cơ sở nằm trong miền cũng tính là có.
 */
export function scopeIntersectsGroup(
  scopes: readonly string[],
  groupCode: string,
  units: readonly ScopeUnit[],
): boolean {
  if (scopes.includes(ALL_SCOPE)) return true;
  if (scopes.length === 0) return false;
  if (scopes.includes(`group-${groupCode}`)) return true;
  const byCode = new Map(units.map((unit) => [unit.code, unit]));
  return scopes.some((scope) => {
    if (scope.startsWith("branch-")) {
      return isDescendantOf(scope.slice("branch-".length), groupCode, byCode);
    }
    if (scope.startsWith("group-")) {
      return isDescendantOf(scope.slice("group-".length), groupCode, byCode);
    }
    return false;
  });
}

/** Nhãn gọn để hiện trên thẻ: tên khi ít, đếm khi nhiều, đủ danh sách ở `title`. */
export function summariseScopes(
  scopes: readonly string[],
  units: readonly ScopeUnit[],
): { label: string; title: string; empty: boolean } {
  if (scopes.includes(ALL_SCOPE)) {
    return { label: "Tất cả chi nhánh", title: "Tất cả chi nhánh", empty: false };
  }
  if (scopes.length === 0) {
    return {
      label: "Chưa gán chi nhánh",
      title: "Concept này chưa gán chi nhánh nào nên sẽ không hiện ở đâu",
      empty: true,
    };
  }
  const byCode = new Map(units.map((unit) => [unit.code, unit]));
  const names = scopes.map((scope) => {
    const code = scope.replace(/^(group|branch)-/, "");
    return byCode.get(code)?.name ?? code;
  });
  const title = names.join(", ");
  return {
    label: names.length <= 2 ? title : `${names.length} chi nhánh`,
    title,
    empty: false,
  };
}
